import React, { useEffect, useMemo } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
	ResizableHandle,
	ResizablePanel,
	ResizablePanelGroup,
} from "@/components/ui/resizable";
import { useActiveInboxAccounts } from "@/hooks/inbox/useConversations";
import { useInboxRealtime } from "@/hooks/inbox/useInboxRealtime";
import { ConversationList } from "./ConversationList";
import { MessageThread } from "./MessageThread";
import { ContextPanel } from "./ContextPanel";
import { EmptyThreadState } from "./EmptyThreadState";
import { useInboxUI } from "./InboxProvider";
import { useFilteredConversations } from "./useFilteredConversations";

/**
 * The full-page unified inbox: a three-pane resizable layout on desktop
 * (conversations · thread · context), a list ⇄ thread stack on mobile.
 */
export const InboxPage: React.FC = () => {
	const isMobile = useIsMobile();
	const { selectedId, setSelectedId, contextPanelOpen, setContextPanelOpen } = useInboxUI();
	const { hasActiveAccounts, isLoading: accountsLoading } = useActiveInboxAccounts();
	const { conversations, allConversations } = useFilteredConversations(hasActiveAccounts);
	useInboxRealtime(hasActiveAccounts);

	const selectedConversation = useMemo(
		() => allConversations.find((conversation) => conversation.id === selectedId),
		[allConversations, selectedId]
	);

	// Keyboard navigation: ↑/↓ or j/k across the filtered list, Escape closes.
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			const target = event.target as HTMLElement | null;
			if (
				target &&
				(target.tagName === "INPUT" ||
					target.tagName === "TEXTAREA" ||
					target.tagName === "SELECT" ||
					target.isContentEditable)
			) {
				return;
			}
			const isNext = event.key === "ArrowDown" || event.key === "j";
			const isPrevious = event.key === "ArrowUp" || event.key === "k";
			if (isNext || isPrevious) {
				if (conversations.length === 0) return;
				event.preventDefault();
				const currentIndex = conversations.findIndex((c) => c.id === selectedId);
				const nextIndex =
					currentIndex === -1
						? 0
						: Math.min(Math.max(currentIndex + (isNext ? 1 : -1), 0), conversations.length - 1);
				setSelectedId(conversations[nextIndex].id);
			} else if (event.key === "Escape" && selectedId) {
				event.preventDefault();
				setSelectedId(null);
			}
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [conversations, selectedId, setSelectedId]);

	const showContext = contextPanelOpen && !!selectedConversation;

	if (isMobile) {
		return (
			<div className="relative h-full min-h-0 overflow-hidden bg-background">
				{selectedConversation ? (
					<div className="h-full animate-slide-in">
						<MessageThread
							conversation={selectedConversation}
							toolbarId="inbox-page-email-toolbar"
							onBack={() => setSelectedId(null)}
							contextPanelOpen={contextPanelOpen}
							onToggleContextPanel={() => setContextPanelOpen(!contextPanelOpen)}
						/>
						<Sheet open={contextPanelOpen} onOpenChange={setContextPanelOpen}>
							<SheetContent side="right" className="w-[85vw] max-w-sm p-0">
								<ContextPanel conversation={selectedConversation} />
							</SheetContent>
						</Sheet>
					</div>
				) : (
					<ConversationList hasActiveAccounts={hasActiveAccounts || accountsLoading} />
				)}
			</div>
		);
	}

	return (
		<ResizablePanelGroup
			direction="horizontal"
			autoSaveId="inbox-panels"
			className="h-full min-h-0 bg-background"
		>
			<ResizablePanel defaultSize={26} minSize={18} maxSize={40} className="min-w-[260px]">
				<ConversationList hasActiveAccounts={hasActiveAccounts || accountsLoading} />
			</ResizablePanel>
			<ResizableHandle />
			<ResizablePanel defaultSize={showContext ? 52 : 74} minSize={35}>
				{selectedConversation ? (
					<MessageThread
						conversation={selectedConversation}
						toolbarId="inbox-page-email-toolbar"
						contextPanelOpen={contextPanelOpen}
						onToggleContextPanel={() => setContextPanelOpen(!contextPanelOpen)}
					/>
				) : (
					<EmptyThreadState />
				)}
			</ResizablePanel>
			{showContext && (
				<>
					<ResizableHandle />
					<ResizablePanel defaultSize={22} minSize={16} maxSize={32} className="min-w-[240px]">
						<ContextPanel conversation={selectedConversation!} />
					</ResizablePanel>
				</>
			)}
		</ResizablePanelGroup>
	);
};
